/* SEARCH-TAG: page-template  */
import React, { useState, useLayoutEffect, useContext } from 'react';
import { graphql } from 'gatsby';
import { shape, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import blogArticleProps from 'TMPropTypes/blogArticleProps';
import HeadTags from 'TMComponents/HeadTags';
import Main from 'TMComponents/Layout/Main';
import { Button } from 'TMComponents/Button';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import BlogCard from 'TMComponents/BlogCard';
import ComponentMapper from 'TMComponents/ComponentMapper';
import contentGrouper from 'TMUtils/contentGrouper';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';

const PageContainer = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 0 ${spacing.medium};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
    }
`;

const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    margin: ${spacing.small} 0 ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        align-items: flex-start;
    }
`;

const PressEnquiriesSection = styled.div`
    margin: ${spacing.xxLarge} 0 0 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};
    margin: 0 0 ${spacing.large} 0;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
        gap: ${spacing.mediumLarge};
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
        gap: ${spacing.mediumLarge};
    }
`;

const LoadMoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin: ${spacing.xxLarge} 0;
`;

const FollowSection = styled.div`
    margin: ${spacing.xxxLarge} 0 ${spacing.xxLarge} 0;
    text-align: center;
`;

const PressPage = ({ data }) => {
    const [pageNo, setPageNo] = useState(1);
    const articles = data.pressReleases.nodes;

    const isBannerAllowed = true;
    const isCrumbsAllowed = true;

    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    useLayoutEffect(() => {
        setIsNavHidden(false);
        setShowBanner(isBannerAllowed);
        setCrumbs({
            show: isCrumbsAllowed,
            crumbList: [{ title: 'Press', link: '/press/' }]
        });
    }, []);

    return (
        <Main
            isBannerAllowed={isBannerAllowed}
            isCrumbsAllowed={isCrumbsAllowed}
            reducedCrumbSpacing>
            <HeadTags tags={data.pageContent.seoMetaTags.tags} />
            <Canonical href="press" />
            <PageContainer>
                {data.pageContent.heroContent && (
                    <HeaderContent>
                        {contentGrouper(data.pageContent.heroContent).map(
                            (group) => (
                                <ComponentMapper
                                    key={group.id || group.__typename}
                                    leftAlign
                                    {...group}
                                />
                            )
                        )}
                    </HeaderContent>
                )}

                {data.pageContent.pressEnquiriesContent &&
                    data.pageContent.pressEnquiriesContent.length > 0 && (
                        <PressEnquiriesSection>
                            {contentGrouper(
                                data.pageContent.pressEnquiriesContent
                            ).map((group) => (
                                <ComponentMapper
                                    key={group.id || group.__typename}
                                    {...group}
                                />
                            ))}
                        </PressEnquiriesSection>
                    )}

                <CardsGrid>
                    {articles.slice(0, 6 * pageNo).map((article) => (
                        <BlogCard
                            key={article.id}
                            title={article.title}
                            slug={article.slug}
                            image={getImage(article.featuredImage)}
                            excerpt={article.summary}
                            tags={article.categories.map(
                                (cat) => cat.name
                            )}
                            variant="press"
                        />
                    ))}
                </CardsGrid>

                {articles.length > 6 * pageNo && (
                    <LoadMoreWrapper>
                        <Button onClick={() => setPageNo(pageNo + 1)}>
                            Load more articles
                        </Button>
                    </LoadMoreWrapper>
                )}

                <FollowSection>
                    {/* More about thinkmoney section - to be implemented */}
                </FollowSection>
            </PageContainer>
        </Main>
    );
};

export const pageQuery = graphql`
    {
        pageContent: datoCmsPressLandingPage {
            seoMetaTags {
                ...GatsbyDatoCmsSeoMetaTags
            }
            heroContent {
                ... on DatoCmsTextStructured {
                    ...textStructuredFields
                }
                ... on DatoCmsButton {
                    ...buttonFields
                }
            }
            pressEnquiriesContent {
                ... on DatoCmsSectionHeading {
                    ...sectionHeadingFields
                }
                ... on DatoCmsPressEnquiriesBlock {
                    ...pressEnquiriesBlockFields
                }
                ... on DatoCmsTextStructured {
                    ...textStructuredFields
                }
            }
        }

        pressReleases: allDatoCmsBlogArticle(
            filter: {
                categories: {
                    elemMatch: { name: { eq: "Press Release" } }
                }
            }
            sort: { fields: date, order: DESC }
            limit: 100
        ) {
            nodes {
                id
                title
                slug
                summary
                featuredImage {
                    gatsbyImageData(
                        width: 400
                        layout: CONSTRAINED
                        placeholder: NONE
                    )
                }
                categories {
                    name
                }
            }
        }
    }
`;

PressPage.propTypes = {
    data: shape({
        pageContent: shape({}),
        pressReleases: shape({
            nodes: arrayOf(shape(blogArticleProps))
        })
    }).isRequired
};

export default PressPage;
